import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateClassification } from "@/hooks/useRequests";
import { PODS, POD_LABELS, PRIORITIES, REQUEST_TYPES } from "@/lib/constants";
import type {
  BlinkRequest,
  ClassificationPayload,
  Pod,
  Priority,
  RequestType,
} from "@/lib/types";

interface ChangeClassificationDialogProps {
  request: BlinkRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Change a request's type, pod and/or priority before approval. Switching type asks
 * for the new type's mandatory field when the request doesn't have it yet.
 */
export function ChangeClassificationDialog({
  request,
  open,
  onOpenChange,
}: ChangeClassificationDialogProps) {
  const { toast } = useToast();
  const { mutate: save, isPending } = useUpdateClassification(request.id);
  const [type, setType] = useState<RequestType>(request.request_type);
  const [pod, setPod] = useState<Pod>(request.pod);
  const [priority, setPriority] = useState<Priority>(request.priority);
  const [requiredText, setRequiredText] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setType(request.request_type);
      setPod(request.pod);
      setPriority(request.priority);
      setRequiredText("");
      setReason("");
    }
  }, [open, request]);

  const typeChanged = type !== request.request_type;
  const podChanged = pod !== request.pod;
  const priorityChanged = priority !== request.priority;

  // The new type's mandatory field, if the request doesn't already have it.
  const requiredField =
    typeChanged && type === "Feature" && !request.expected_outcome?.trim()
      ? { key: "expected_outcome" as const, label: "Expected Outcome" }
      : typeChanged && type === "Defect" && !request.steps_to_reproduce?.trim()
        ? { key: "steps_to_reproduce" as const, label: "Steps to Reproduce" }
        : null;

  const canSave =
    (typeChanged || podChanged || priorityChanged) &&
    pod !== "Unknown" &&
    (!requiredField || requiredText.trim().length > 0) &&
    (!priorityChanged || reason.trim().length > 0);

  const handleSave = () => {
    if (!canSave) return;
    const payload: ClassificationPayload = {};
    if (typeChanged) payload.request_type = type;
    if (podChanged) payload.pod = pod;
    if (priorityChanged) payload.priority = priority;
    if (requiredField) payload[requiredField.key] = requiredText.trim();
    if (reason.trim()) payload.reason = reason.trim();
    save(payload, {
      onSuccess: () => {
        toast({
          title: "Request updated",
          description:
            "The change has been recorded and shared with the requester.",
        });
        onOpenChange(false);
      },
      onError: (err) =>
        toast({
          title: "Could not update request",
          description: err.message,
          variant: "destructive",
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change type, product area or priority</DialogTitle>
          <DialogDescription>
            Available until the request is approved. The requester will see the
            change in the conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="classification-type">Request type</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as RequestType)}
            >
              <SelectTrigger id="classification-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {requiredField && (
            <div className="space-y-1.5">
              <Label htmlFor="classification-required">
                {requiredField.label}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="classification-required"
                value={requiredText}
                onChange={(e) => setRequiredText(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Required for a {type.toLowerCase()} request.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="classification-pod">Product area</Label>
            <Select value={pod} onValueChange={(v) => setPod(v as Pod)}>
              <SelectTrigger id="classification-pod">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {request.pod === "Unknown" && (
                  <SelectItem value="Unknown" disabled>
                    {POD_LABELS.Unknown}
                  </SelectItem>
                )}
                {PODS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {POD_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="classification-priority">Priority</Label>
            <Select
              value={priority}
              onValueChange={(v) => setPriority(v as Priority)}
            >
              <SelectTrigger id="classification-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="classification-reason">
              Reason{" "}
              {priorityChanged ? (
                <span className="text-destructive">*</span>
              ) : (
                "(optional)"
              )}
            </Label>
            <Textarea
              id="classification-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder={
                priorityChanged
                  ? "Why is the priority changing? The requester will be emailed."
                  : "e.g. This is a defect in existing behaviour, not a new feature"
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
